import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import DemoScheduler from "@/components/DemoScheduler";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import {
  ArrowRight,
  Calendar,
  Clock,
  User,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  CheckCircle2,
  Shield,
  Workflow,
  MessageSquare,
  Timer,
  Layers,
  Users,
  Bot,
} from "lucide-react";

const BlogPostSolicitacoes = () => {
  const { isOpen, setIsOpen } = useBlogContactPopup(3000);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Módulo de Solicitações da Aroan | Service Requests Enterprise"
        description="Conheça o módulo de Solicitações do Vértice Aroan: multi-tenant com RLS, SLA inteligente, chat embutido, auditoria completa e integração nativa com CMDB, KB e CSAT."
        canonical="https://aroan.com.br/blog/modulo-solicitacoes-aroan"
        ogImage="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&auto=format&fit=crop"
        author="Equipe Aroan"
        publishedTime="2026-04-28T00:00:00-03:00"
        keywords="solicitações, service request, ITSM, service desk, SLA, multi-tenant, RLS, CMDB, CSAT, base de conhecimento, aroan, vértice"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://aroan.com.br" },
          { name: "Blog", url: "https://aroan.com.br/blog" },
          { name: "Módulo de Solicitações da Aroan" },
        ]}
      />
      <Header />

      <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              Service Management
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
              Módulo de <span className="text-primary">Solicitações</span> do Vértice Aroan
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">Equipe Aroan</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span>28 Abr, 2026</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span>10 min de leitura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in bg-card flex items-center justify-center">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&auto=format&fit=crop"
              alt="Equipe de service desk gerenciando solicitações na plataforma Aroan"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Left Column */}
              <div className="space-y-8">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Visão Geral</h2>
                  <p className="lead text-xl text-muted-foreground leading-relaxed font-medium">
                    O módulo de <strong>Solicitações</strong> é o coração transacional do Aroan para registro, tratamento e governança de pedidos de serviço feitos pelos usuários finais ao Service Desk.
                  </p>
                  <p>
                    Diferente de incidentes (algo quebrado) e problemas (causa raiz recorrente), uma <strong>Solicitação</strong> representa um pedido planejado — instalação de software, criação de acesso, troca de equipamento, requisição de material ou dúvidas operacionais.
                  </p>
                  <p>
                    O módulo foi projetado para ser <strong>multi-tenant nativo</strong>, <strong>self-service first</strong>, <strong>SLA-driven</strong>, <strong>auditável</strong>, <strong>colaborativo</strong> e profundamente <strong>integrado</strong> a Categorias, Grupos, CMDB, CSAT e Base de Conhecimento.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Pilares do módulo</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      "Multi-tenant com RLS no PostgreSQL",
                      "Self-service em menos de 30 segundos",
                      "SLA com pausa inteligente por status",
                      "Chat embutido com anexos em tempo real",
                      "Auditoria completa e imutável",
                      "Integração nativa com CMDB e KB",
                    ].map((item) => (
                      <div key={item} className="flex gap-3 rounded-lg border border-border/60 p-4">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground m-0">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">1. Arquitetura segura e multi-tenant</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    A entidade central <code>solicitacoes</code> unifica os tipos de tickets (solicitação, incidente, problema, requisição e mudança) sob uma máquina de estado coerente, com isolamento total por <code>client_id</code>.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Row Level Security ativada em todas as tabelas auxiliares.</li>
                    <li>Função <code>user_has_access_to_client</code> valida o tenant do usuário logado.</li>
                    <li>Solicitante vê só os próprios chamados; técnico vê apenas dos seus grupos.</li>
                    <li>Cliente fica <strong>readonly após criação</strong>, garantindo integridade de tenant.</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Layers className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">2. Catálogo de Serviços por Categorias</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    As categorias funcionam como o catálogo de serviços do Aroan, com campos dinâmicos, grupo padrão e SLA vinculado — tudo aplicado automaticamente no momento da abertura.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border/60 p-4">
                      <h4 className="font-semibold text-foreground mb-2">Campos customizados</h4>
                      <p className="text-sm text-muted-foreground m-0">
                        Texto, select, número, data e multiselect dinâmicos por categoria via JSONB.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 p-4">
                      <h4 className="font-semibold text-foreground mb-2">Roteamento automático</h4>
                      <p className="text-sm text-muted-foreground m-0">
                        Grupo responsável e SLA herdados da categoria, com hierarquia pai/filho.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">3. Duas interfaces, duas personas</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    O módulo se divide entre o <strong>Portal do Usuário</strong> (ultra-simplificado) e o <strong>Painel Administrativo</strong> (completo para analistas e gestores).
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Portal: abertura em poucos cliques com herança automática de tenant e canal.</li>
                    <li>Painel: filtros avançados, estatísticas, exportação CSV e badges de SLA.</li>
                    <li>Click no número abre o modal de edição com 3 abas operacionais.</li>
                  </ul>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Centro nervoso operacional</h2>
                  <p>
                    O <strong>EditSolicitacaoDialog</strong> concentra toda a operação do atendimento em três abas coordenadas — <strong>Formulário</strong>, <strong>Chat</strong> e <strong>Logs</strong> — garantindo que o analista nunca precise alternar entre sistemas paralelos.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Timer className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">4. Motor de SLA multidimensional</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    O SLA é resolvido por hierarquia (Categoria → Grupo → Tenant) e considera horário comercial, finais de semana e feriados configuráveis.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong>Tempo de Resposta:</strong> até o primeiro atendimento.</li>
                    <li><strong>Tempo de Resolução:</strong> até a aplicação da solução.</li>
                    <li><strong>Pausa automática</strong> quando o status vai para <code>pendente</code>.</li>
                    <li>Indicadores 🟢 Dentro · 🟡 Em risco · 🔴 Violado em tempo real.</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Workflow className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">5. Máquina de estado rígida</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    O fluxo respeita transições controladas (<code>statusTransitions.ts</code>) que protegem a integridade operacional e disparam ações automáticas em pontos-chave.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><code>aberta → em_andamento</code> marca data do primeiro contato.</li>
                    <li><code>em_andamento → resolvida</code> exige solução aplicada preenchida.</li>
                    <li><code>resolvida → fechada</code> dispara CSAT e geração de KB.</li>
                    <li>Auto-fechamento via cron para resolvidas sem retorno do solicitante.</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageSquare className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">6. Colaboração e auditoria embutidas</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Chat em tempo real entre solicitante e atendente (com anexos via Supabase Storage) e log imutável de toda alteração de campo — essencial para compliance ITIL, SOX e LGPD.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Bot className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">7. Integrações nativas com a VIA</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    O módulo é o hub que conecta CMDB, Base de Conhecimento, CSAT, Plano de Ação e webhooks externos. A <strong>VIA</strong>, IA proprietária do Aroan, gera artigos de KB automaticamente ao fechar solicitações marcadas com <code>gerar_kb = true</code>.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Vinculação a Itens de Configuração (CIs) na timeline do CMDB.</li>
                    <li>CSAT disparado automaticamente no fechamento.</li>
                    <li>Webhooks para Teams, e-mail e — em breve — WhatsApp e Telegram.</li>
                  </ul>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Métricas que o módulo entrega</h2>
                  <p>
                    Volume de chamados, MTTR, MTTA, % de SLA cumprido, backlog por idade, top categorias e solicitantes, taxa de reincidência, CSAT médio e heatmap de demanda — tudo exportável em PDF, Excel e CSV, com agendamento recorrente por e-mail.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-background border border-primary/10 rounded-2xl p-6">
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Conclusão</h3>
                  <p className="text-muted-foreground mb-4">
                    O módulo de Solicitações do Aroan vai além de um sistema de tickets: ele garante isolamento de tenant, governança de SLA, auditoria completa e captura sistemática do conhecimento gerado pela operação.
                  </p>
                  <p className="text-muted-foreground">
                    Para empresas de médio e grande porte, isso significa <strong>menos retrabalho, mais previsibilidade e Service Management que respeita o tempo do cliente</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-y border-border py-6 mb-12 mt-12">
              <span className="font-semibold">Compartilhe este artigo:</span>
              <div className="flex gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, "_blank")}
                >
                  <Linkedin className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Confira o módulo de Solicitações do Vértice Aroan!")}`, "_blank")}
                >
                  <Twitter className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank")}
                >
                  <Facebook className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: "Módulo de Solicitações da Aroan",
                          text: "Confira este artigo sobre o módulo de Solicitações do Vértice Aroan!",
                          url: window.location.href,
                        });
                      } catch (err) {
                        console.error("Error sharing:", err);
                      }
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert("Link copiado para a área de transferência!");
                    }
                  }}
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Quer transformar o seu Service Desk?
              </h2>
              <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                Veja na prática como o módulo de Solicitações do Aroan unifica chamados, SLA, CMDB, KB e CSAT em uma única plataforma multi-tenant.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                  Agende uma Demonstração
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/materiais', '_self')}>
                  Ver Materiais
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
    </div>
  );
};

export default BlogPostSolicitacoes;
